import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, MapPin, CalendarDays, Loader2, ShieldCheck, ChevronDown } from "lucide-react";
import { QRCodeCanvas } from "qrcode.react";
import { api, apiError, cachedGet, fileUrl } from "@/lib/api";
import { toast } from "sonner";
import Reveal from "@/components/Reveal";
import SectionHeading from "@/components/SectionHeading";

const inputCls = "w-full bg-ink border border-bone/15 px-4 py-3.5 text-bone placeholder:text-ash/60 focus:border-saffron focus:outline-none transition-colors";

function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

function TextBlock({ title, text, testid }) {
  if (!text) return null;
  return (
    <div data-testid={testid}>
      <p className="font-display text-xs tracking-[0.3em] text-saffron uppercase">{title}</p>
      <div className="mt-4 space-y-2.5">
        {text.split("\n").filter(Boolean).map((line, i) => (
          <p key={i} className="text-sm text-bone/80 leading-relaxed">{line}</p>
        ))}
      </div>
    </div>
  );
}

function TripHeading({ trip }) {
  return (
    <>
      <p className="font-display text-xs tracking-[0.35em] text-saffron uppercase flex items-center gap-2">
        <MapPin size={14} /> A journey with the band
      </p>
      <h1 className="mt-4 font-serif text-5xl sm:text-6xl text-bone leading-tight" data-testid="yatra-title">
        {trip?.title || "Vrindavan Yatra"}
      </h1>
      {trip?.date && (
        <p className="mt-4 flex items-center gap-2 text-lg text-bone/80" data-testid="yatra-date">
          <CalendarDays size={17} className="text-saffron" /> {trip.date}
        </p>
      )}
      {trip?.multi_date && trip?.departure_dates && (
        <div className="mt-3 flex flex-wrap gap-2" data-testid="yatra-departures">
          {trip.departure_dates.split("\n").filter(Boolean).map((d) => (
            <span key={d} className="text-xs border border-saffron/40 text-saffron px-3 py-1.5">{d}</span>
          ))}
        </div>
      )}
    </>
  );
}

const ACTIVITY_ICONS = [
  [/sunrise|morning|pratah/i, "🌅"],
  [/aarti/i, "🪔"],
  [/kirtan|bhajan|music|singing|concert/i, "🎶"],
  [/darshan|temple|mandir|bihari/i, "🛕"],
  [/parikrama|walk|govardhan/i, "🚶"],
  [/boat|yamuna|river|ghat/i, "🛶"],
  [/bus|coach|travel|depart|journey|drive|arriv/i, "🚌"],
  [/breakfast|lunch|dinner|meal|prasad|food|bhojan|chai|tea/i, "🍛"],
  [/stay|check.?in|hotel|ashram|room|rest|leisure/i, "🛏️"],
  [/satsang|discourse|katha|meditation|prayer/i, "🙏"],
  [/market|shopping|bazaar/i, "🛍️"],
  [/return|back home|drop/i, "🏠"],
];
const TIME_RE = /\b(\d{1,2}(?::\d{2})?\s?(?:a\.?m\.?|p\.?m\.?))\b/i;

// Splits a day's description into scan-friendly activities:
// one per line, or comma/semicolon-separated. A leading emoji typed by the
// admin is used as-is; otherwise an icon is picked by keyword. Times like
// "6:30 AM" are pulled out into a highlight chip.
function parseActivities(text) {
  if (!text) return [];
  let parts = text.split("\n").map((s) => s.trim()).filter(Boolean);
  if (parts.length <= 1) parts = text.split(/[,;•]/).map((s) => s.trim()).filter(Boolean);
  return parts.map((raw) => {
    let label = raw;
    let icon = null;
    let time = null;
    const emojiMatch = label.match(/^(\p{Extended_Pictographic}\uFE0F?)\s*/u);
    if (emojiMatch) {
      icon = emojiMatch[1];
      label = label.slice(emojiMatch[0].length).trim();
    }
    const timeMatch = label.match(TIME_RE);
    if (timeMatch) {
      time = timeMatch[1].replace(/\s+/g, "").toUpperCase();
      label = label.replace(timeMatch[0], "").replace(/\s+at\s*$/i, "").replace(/^[\s\-–—:,]+|[\s\-–—,]+$/g, "").trim();
    }
    if (!icon) {
      const hit = ACTIVITY_ICONS.find(([re]) => re.test(label));
      icon = hit ? hit[1] : "🌸";
    }
    return { icon, time, label: label.charAt(0).toUpperCase() + label.slice(1) };
  }).filter((a) => a.label);
}

function ItineraryTimeline({ items }) {
  const [openIdx, setOpenIdx] = useState(0);
  return (
    <div className="relative" data-testid="yatra-itinerary">
      <div className="absolute left-[9px] top-3 bottom-3 w-px bg-gradient-to-b from-saffron/60 via-saffron/25 to-transparent" aria-hidden="true" />
      <div>
        {items.map((item, i) => {
          const open = openIdx === i;
          return (
            <div key={i} className="relative pl-10" data-testid={`yatra-itinerary-item-${i}`}>
              <span aria-hidden="true"
                className={`absolute left-0 top-3 w-[19px] h-[19px] rounded-full border flex items-center justify-center transition-colors ${open ? "border-saffron bg-saffron/15" : "border-saffron/40 bg-ink"}`}>
                <span className={`w-1.5 h-1.5 rounded-full transition-colors ${open ? "bg-saffron" : "bg-saffron/50"}`} />
              </span>
              <button type="button" onClick={() => setOpenIdx(open ? -1 : i)} aria-expanded={open}
                className="w-full flex items-center justify-between gap-4 py-2.5 text-left group"
                data-testid={`yatra-itinerary-toggle-${i}`}>
                <span className={`font-serif text-lg md:text-xl transition-colors ${open ? "text-saffron" : "text-bone group-hover:text-gold"}`}>
                  {item.title}
                </span>
                <ChevronDown size={16} className={`shrink-0 text-saffron/70 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
              </button>
              <div className={`grid transition-[grid-template-rows,opacity] duration-300 ease-out ${open ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`}>
                <div className="overflow-hidden">
                  {item.description ? (
                    <ul className="mb-5 mr-2 divide-y divide-bone/10 border-b border-bone/10" data-testid={`yatra-itinerary-desc-${i}`}>
                      {parseActivities(item.description).map((a, j) => (
                        <li key={j} className="flex items-baseline gap-3 py-2.5" data-testid={`yatra-activity-${i}-${j}`}>
                          <span className="text-sm leading-none shrink-0" aria-hidden="true">{a.icon}</span>
                          {a.time && (
                            <span className="shrink-0 font-display text-[11px] tracking-wider text-saffron whitespace-nowrap" data-testid={`yatra-activity-time-${i}-${j}`}>{a.time}</span>
                          )}
                          <span className="text-sm text-bone/75 leading-relaxed">{a.label}</span>
                        </li>
                      ))}
                    </ul>
                  ) : <div className="pb-2" />}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function YatraPage() {
  const [trip, setTrip] = useState(null);
  const [packages, setPackages] = useState([]);
  const [pkgId, setPkgId] = useState("");
  const [form, setForm] = useState({ name: "", phone: "" });
  const [seats, setSeats] = useState(1);
  const [payOpt, setPayOpt] = useState("full");
  const [processing, setProcessing] = useState(false);
  const [ticket, setTicket] = useState(null);

  const load = () => cachedGet("/yatra", 5000).then(({ data }) => {
    setTrip(data.trip);
    setPackages(data.packages);
    if (!pkgId && data.packages.length) setPkgId(data.packages[0].id);
  }).catch(() => {});

  useEffect(() => { loadRazorpayScript(); load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const pkg = packages.find((p) => p.id === pkgId);
  const total = pkg ? pkg.price * seats : 0;
  const payable = payOpt === "full" ? total : Math.round(total / 2);
  const balance = total - payable;
  const hasBanner = !!trip?.banner_image_url;

  const book = async () => {
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Please enter your name and phone number");
      return;
    }
    setProcessing(true);
    try {
      const { data } = await api.post("/yatra/create-order", {
        name: form.name, phone: form.phone, package_id: pkgId, seats, payment_option: payOpt,
      });
      if (data.demo) {
        const res = await api.post("/bookings/verify", { booking_id: data.booking_id });
        setTicket(res.data);
        return;
      }
      const rzp = new window.Razorpay({
        key: data.key_id, amount: data.amount, currency: "INR",
        name: "The Band Sumiran — Vrindavan Yatra",
        description: `${pkg.name} · ${seats} seat${seats > 1 ? "s" : ""} · ${payOpt === "advance" ? "50% advance" : "full payment"}`,
        order_id: data.order_id,
        prefill: { name: form.name, contact: form.phone },
        theme: { color: "#D97736" },
        handler: async (resp) => {
          const res = await api.post("/bookings/verify", {
            booking_id: data.booking_id,
            razorpay_payment_id: resp.razorpay_payment_id,
            razorpay_signature: resp.razorpay_signature,
          });
          setTicket(res.data);
          load();
        },
        modal: { ondismiss: () => setProcessing(false) },
      });
      rzp.open();
      setProcessing(false);
    } catch (err) {
      toast.error(apiError(err, "Could not start booking"));
      setProcessing(false);
    }
  };

  if (ticket) {
    return (
      <div className="min-h-screen bg-ink text-bone flex items-center justify-center px-6" data-testid="yatra-success">
        <div className="max-w-md w-full border border-saffron/40 bg-surface p-8 text-center">
          <p className="font-hindi text-3xl text-saffron/70">सुमिरन</p>
          <h2 className="mt-4 font-serif text-3xl text-bone">Your yatra seat is booked</h2>
          <p className="mt-2 text-sm text-ash">Confirmation sent on WhatsApp to {ticket.phone}</p>
          <div className="mt-6 inline-block bg-bone p-3">
            <QRCodeCanvas value={ticket.ticket_code} size={150} data-testid="yatra-qr" />
          </div>
          <p className="mt-4 font-display text-2xl tracking-[0.3em] text-gold" data-testid="yatra-ticket-code">{ticket.ticket_code}</p>
          <p className="mt-2 text-sm text-bone/70">{ticket.event_name} · {ticket.quantity} seat{ticket.quantity > 1 ? "s" : ""}</p>
          {ticket.balance_due > 0 && (
            <p className="mt-3 text-xs text-saffron border border-saffron/40 bg-saffron/5 px-3 py-2" data-testid="yatra-balance-note">
              Balance of ₹{(ticket.balance_due / 100).toLocaleString("en-IN")} to be paid before departure.
            </p>
          )}
          <Link to="/" className="mt-6 inline-block text-xs text-ash hover:text-saffron transition-colors" data-testid="yatra-home-link">Back to home</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-ink text-bone" data-testid="yatra-page">
      <div className="noise-overlay" aria-hidden="true" />
      <header className="px-6 md:px-12 h-[72px] flex items-center justify-between border-b border-bone/10 sticky top-0 bg-ink/90 backdrop-blur-xl z-40">
        <Link to="/" className="flex items-baseline gap-2" data-testid="yatra-logo">
          <span className="font-hindi text-2xl text-saffron">सुमिरन</span>
          <span className="font-display text-sm tracking-[0.25em] text-bone">THE BAND SUMIRAN</span>
        </Link>
        <Link to="/" className="text-xs text-ash hover:text-saffron transition-colors" data-testid="yatra-back-home">Back to site</Link>
      </header>

      {hasBanner && (
        <section className="relative min-h-[54vh] md:min-h-[64vh] flex items-end overflow-hidden" data-testid="yatra-banner">
          <img src={fileUrl(trip.banner_image_url)} alt={trip?.title || "Vrindavan Yatra"} fetchpriority="high" decoding="async"
            className="absolute inset-0 w-full h-full object-cover" data-testid="yatra-banner-img" />
          <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/55 to-ink/20" aria-hidden="true" />
          <div className="relative w-full max-w-6xl mx-auto px-6 md:px-12 pt-28 pb-12 md:pb-16">
            <Reveal y={26}>
              <TripHeading trip={trip} />
            </Reveal>
          </div>
        </section>
      )}

      <main className="px-6 md:px-12 py-14 md:py-20 max-w-6xl mx-auto">
        {!hasBanner && (
          <Reveal>
            <TripHeading trip={trip} />
            {trip?.description && (
              <p className="mt-6 text-base md:text-lg text-ash leading-relaxed max-w-3xl" data-testid="yatra-description">{trip.description}</p>
            )}
          </Reveal>
        )}
        {hasBanner && trip?.description && (
          <Reveal>
            <p className="text-base md:text-lg text-ash leading-relaxed max-w-3xl" data-testid="yatra-description">{trip.description}</p>
          </Reveal>
        )}

        <div className="mt-16 grid grid-cols-1 lg:grid-cols-5 gap-12 md:gap-16">
          <Reveal className="lg:col-span-3">
            <p className="font-display text-xs tracking-[0.3em] text-saffron uppercase">Itinerary</p>
            <h2 className="mt-3 font-serif text-2xl md:text-3xl text-bone">The journey, <em className="text-gold font-light">day by day</em></h2>
            <div className="mt-7">
              {trip?.itinerary_items?.length ? (
                <ItineraryTimeline items={trip.itinerary_items} />
              ) : trip?.itinerary ? (
                <div className="space-y-2.5" data-testid="yatra-itinerary">
                  {trip.itinerary.split("\n").filter(Boolean).map((line, i) => (
                    <p key={i} className="text-sm text-bone/80 leading-relaxed">{line}</p>
                  ))}
                </div>
              ) : null}
            </div>
          </Reveal>
          <div className="lg:col-span-2 space-y-10">
            <Reveal delay={0.08}><TextBlock title="Inclusions" text={trip?.inclusions} testid="yatra-inclusions" /></Reveal>
            <Reveal delay={0.16}><TextBlock title="Exclusions" text={trip?.exclusions} testid="yatra-exclusions" /></Reveal>
          </div>
        </div>

        <div className="mt-20 border-t border-bone/10 pt-14">
          <SectionHeading overline="Book your seat" title="Choose your" italic="stay" testid="yatra-packages-heading" />
          <div className="mt-10 grid grid-cols-1 lg:grid-cols-5 gap-10 lg:gap-14 items-start">
            <div className="lg:col-span-3 space-y-3" data-testid="yatra-package-list">
              {packages.map((p, i) => {
                const sel = p.id === pkgId;
                const soldOut = p.remaining_seats <= 0;
                return (
                  <div key={p.id} role="button" tabIndex={0}
                    onClick={() => !soldOut && setPkgId(p.id)}
                    onKeyDown={(e) => e.key === "Enter" && !soldOut && setPkgId(p.id)}
                    className={`w-full flex items-center justify-between gap-3 px-5 py-5 border text-left transition-colors ${soldOut ? "opacity-40 cursor-not-allowed border-bone/10" : sel ? "border-saffron bg-saffron/10 cursor-pointer" : "border-bone/15 hover:border-bone/35 cursor-pointer"}`}
                    data-testid={`yatra-package-${i}`}>
                    <span className="flex items-center gap-3">
                      <span className={`w-4 h-4 rounded-full border flex items-center justify-center shrink-0 ${sel && !soldOut ? "border-saffron" : "border-bone/30"}`}>
                        {sel && !soldOut && <Check size={10} className="text-saffron" />}
                      </span>
                      <span>
                        <span className="text-sm text-bone block">{p.name}</span>
                        {soldOut && (
                          <span className="text-xs text-saffron mt-0.5 block" data-testid={`yatra-soldout-${i}`}>Sold out</span>
                        )}
                      </span>
                    </span>
                    <span className="font-serif text-xl text-gold whitespace-nowrap">₹{p.price.toLocaleString("en-IN")}<span className="text-xs text-ash font-sans"> /person</span></span>
                  </div>
                );
              })}
            </div>

            <div className="lg:col-span-2 lg:sticky lg:top-24">
              <div className="border border-bone/15 bg-surface p-6 md:p-7" data-testid="yatra-booking-box">
                <p className="font-display text-xs tracking-[0.3em] text-saffron uppercase">Reserve your seat</p>
                <div className="mt-5 space-y-4">
                  <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Full name" className={inputCls} data-testid="yatra-name-input" />
                  <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} type="tel"
                    placeholder="Phone — confirmation on WhatsApp" className={inputCls} data-testid="yatra-phone-input" />
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-ash">Seats</span>
                    <select value={seats} onChange={(e) => setSeats(Number(e.target.value))}
                      className="bg-ink border border-bone/15 px-3 py-2 text-bone focus:border-saffron focus:outline-none" data-testid="yatra-seats-select">
                      {[1, 2, 3, 4, 5, 6].map((n) => <option key={n} value={n} className="bg-ink">{n}</option>)}
                    </select>
                  </div>
                  <div className="grid grid-cols-2 gap-2" data-testid="yatra-payment-options">
                    {[["full", "Pay Full Amount"], ["advance", "Pay 50% Advance"]].map(([v, label]) => (
                      <button type="button" key={v} onClick={() => setPayOpt(v)}
                        className={`py-3.5 text-xs font-bold tracking-wide border transition-colors ${payOpt === v ? "border-saffron bg-saffron/10 text-saffron" : "border-bone/15 text-ash hover:border-bone/35"}`}
                        data-testid={`yatra-pay-${v}`}>
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="mt-5 pt-5 border-t border-bone/10 space-y-1.5">
                  <div className="flex justify-between text-sm"><span className="text-ash">{pkg?.name || "—"} × {seats}</span><span className="text-bone/70">₹{total.toLocaleString("en-IN")}</span></div>
                  {payOpt === "advance" && (
                    <div className="flex justify-between text-xs"><span className="text-ash">Balance before departure</span><span className="text-saffron" data-testid="yatra-balance">₹{balance.toLocaleString("en-IN")}</span></div>
                  )}
                  <div className="flex justify-between pt-1.5">
                    <span className="text-sm text-ash">Pay now</span>
                    <span className="font-serif text-3xl text-gold" data-testid="yatra-total">₹{payable.toLocaleString("en-IN")}</span>
                  </div>
                </div>
                <button onClick={book} disabled={processing || !pkg || pkg.remaining_seats <= 0}
                  className="mt-5 w-full py-4 bg-saffron text-ink font-medium tracking-wide hover:bg-gold transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="yatra-book-btn">
                  {processing ? <Loader2 size={17} className="animate-spin" /> : null}
                  {processing ? "Creating order…" : `Pay ₹${payable.toLocaleString("en-IN")} — Book Seat${seats > 1 ? "s" : ""}`}
                </button>
                <p className="mt-3 flex items-center justify-center gap-2 text-xs text-ash">
                  <ShieldCheck size={13} className="text-saffron" /> Secure payment via Razorpay · Confirmation on WhatsApp
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
